import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import { useForm, Controller } from 'react-hook-form'
import axios from 'axios'

import { Button } from '../../ui/button'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select'
import Input from '../Input/Input'

function StaffEditForm({ staffMember, facilityId, onSuccess }) {
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState(null)

  const {
    handleSubmit,
    control,
    reset,
    formState: { errors }
  } = useForm({
    defaultValues: {
      fullName: '',
      preferredName: '',
      email: '',
      phone: '',
      permissionLevel: 'staff'
    }
  })

  useEffect(() => {
    if (staffMember) {
      const attrs =
        (staffMember && staffMember.data && staffMember.data.attributes) ||
        (staffMember && staffMember.attributes) ||
        {}

      reset({
        fullName: attrs.fullName || '',
        preferredName: attrs.preferredName || '',
        email: attrs.email || '',
        phone: formatPhoneForDisplay(attrs.phone) || '',
        permissionLevel: attrs.permissionLevel || 'staff'
      })
    }
  }, [staffMember, reset])

  function formatPhoneForDisplay(phone) {
    if (!phone) return ''
    const cleaned = phone.replace(/\D/g, '')
    if (cleaned.length === 11 && cleaned.startsWith('1')) {
      const areaCode = cleaned.slice(1, 4)
      const middle = cleaned.slice(4, 7)
      const last = cleaned.slice(7, 11)
      return `${areaCode}-${middle}-${last}`
    }
    if (cleaned.length === 10) {
      const areaCode = cleaned.slice(0, 3)
      const middle = cleaned.slice(3, 6)
      const last = cleaned.slice(6, 10)
      return `${areaCode}-${middle}-${last}`
    }
    return phone
  }

  function formatPhoneAsTyping(value) {
    const cleaned = value.replace(/\D/g, '')
    const trimmed = cleaned.slice(0, 10)

    if (trimmed.length >= 6) {
      return `${trimmed.slice(0, 3)}-${trimmed.slice(3, 6)}-${trimmed.slice(6)}`
    } else if (trimmed.length >= 3) {
      return `${trimmed.slice(0, 3)}-${trimmed.slice(3)}`
    }
    return trimmed
  }

  function formatPhoneForBackend(phone) {
    if (!phone) return null
    const cleaned = phone.replace(/\D/g, '')
    if (cleaned.length === 10) {
      return `+1${cleaned}`
    }
    return null
  }

  function getPermissionFlags(permissionLevel) {
    switch (permissionLevel) {
      case 'director':
        return { admin: true, coach: true }
      case 'coach':
        return { admin: false, coach: true }
      case 'staff':
        return { admin: false, coach: false }
      default:
        return { admin: false, coach: false }
    }
  }

  async function onSubmit(data) {
    setIsSubmitting(true)
    setError(null)

    try {
      const permissionFlags = getPermissionFlags(data.permissionLevel)

      const staffId = (staffMember.data && staffMember.data.id) || staffMember.id

      const formattedPhone = formatPhoneForBackend(data.phone)

      const userUpdatePayload = {
        user: {
          full_name: data.fullName,
          preferred_name: data.preferredName,
          email: data.email,
          phone: formattedPhone
        }
      }

      await axios.patch(`/api/facilities/${facilityId}/users/${staffId}`, userUpdatePayload)

      const updatePermissionsResponse = await axios.patch(`/api/facilities/${facilityId}/coaches/${staffId}`, {
        coach: permissionFlags
      })

      if (onSuccess) {
        onSuccess(updatePermissionsResponse.data)
      }

      setIsSubmitting(false)
    } catch (err) {
      setError(
        (err.response && err.response.data && err.response.data.errors && err.response.data.errors.join(', ')) ||
          'An error occurred while updating the staff member'
      )
      setIsSubmitting(false)
    }
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-3">
      {error && (
        <div className="p-4 rounded-md bg-red-50 border border-red-200">
          <p className="text-sm text-red-800">{error}</p>
        </div>
      )}

      <div>
        <label htmlFor="fullName" className="block text-sm font-medium text-gray-700 mb-1">
          Full Name
        </label>
        <Controller
          name="fullName"
          control={control}
          rules={{ required: 'Full name is required' }}
          render={({ field }) => (
            <Input id="fullName" type="text" {...field} className={errors.fullName ? 'border-red-500' : ''} />
          )}
        />
        {errors.fullName && <p className="mt-1 text-sm text-red-600">{errors.fullName.message}</p>}
      </div>

      <div>
        <label htmlFor="preferredName" className="block text-sm font-medium text-gray-700 mb-1">
          Preferred Name
        </label>
        <Controller
          name="preferredName"
          control={control}
          rules={{ required: 'Preferred name is required' }}
          render={({ field }) => (
            <Input id="preferredName" type="text" {...field} className={errors.preferredName ? 'border-red-500' : ''} />
          )}
        />
        {errors.preferredName && <p className="mt-1 text-sm text-red-600">{errors.preferredName.message}</p>}
      </div>

      <div>
        <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
          Email
        </label>
        <Controller
          name="email"
          control={control}
          rules={{
            required: 'Email is required',
            pattern: {
              value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
              message: 'Invalid email address'
            }
          }}
          render={({ field }) => (
            <Input id="email" type="text" {...field} className={errors.email ? 'border-red-500' : ''} />
          )}
        />
        {errors.email && <p className="mt-1 text-sm text-red-600">{errors.email.message}</p>}
      </div>

      <div>
        <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">
          Phone
        </label>
        <Controller
          name="phone"
          control={control}
          rules={{
            pattern: {
              value: /^\d{3}-\d{3}-\d{4}$/,
              message: 'Phone must be 10 digits'
            }
          }}
          render={({ field: { onChange, value, ...field } }) => (
            <Input
              id="phone"
              type="text"
              {...field}
              value={value || ''}
              onChange={(e) => {
                const formatted = formatPhoneAsTyping(e.target.value)
                onChange(formatted)
              }}
              placeholder="555-123-4567"
              className={errors.phone ? 'border-red-500' : ''}
            />
          )}
        />
        {errors.phone && <p className="mt-1 text-sm text-red-600">{errors.phone.message}</p>}
        <p className="mt-1 text-sm text-gray-500">Enter 10 digits (will auto-format)</p>
      </div>

      <div>
        <label htmlFor="permissionLevel" className="block text-sm font-medium text-gray-700 mb-1">
          Permission Level
        </label>
        <Controller
          name="permissionLevel"
          control={control}
          rules={{ required: 'Permission level is required' }}
          render={({ field }) => (
            <Select onValueChange={field.onChange} value={field.value}>
              <SelectTrigger className={errors.permissionLevel ? 'border-red-500' : ''}>
                <SelectValue placeholder="Select permission level" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="staff">Staff</SelectItem>
                <SelectItem value="coach">Coach</SelectItem>
                <SelectItem value="director">Director</SelectItem>
              </SelectContent>
            </Select>
          )}
        />
        {errors.permissionLevel && <p className="mt-1 text-sm text-red-600">{errors.permissionLevel.message}</p>}
        <p className="mt-2 text-sm text-gray-500">
          Staff: Basic access • Coach: Can manage classes • Director: Full facility access
        </p>
      </div>

      <div className="pt-2">
        <Button type="submit" disabled={isSubmitting} className="w-full" size="lg">
          {isSubmitting ? 'Saving Changes...' : 'Save Changes'}
        </Button>
      </div>
    </form>
  )
}

StaffEditForm.propTypes = {
  staffMember: PropTypes.object.isRequired,
  facilityId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  onSuccess: PropTypes.func
}

export default StaffEditForm
